import type { DataProvider } from "@refinedev/core";
import { api } from "../lib/api";

/**
 * Refine data provider backed by the PriceWatch FastAPI backend.
 * Handles standard CRUD for admin resources (brands, products, retailers, categories).
 *
 * Analytical endpoints (feed, grid, history, comparison) are NOT routed through
 * this provider — they use useCustom() in each page because they have non-standard
 * response shapes (cursor pagination, nested series, price gaps).
 */
const dataProvider: DataProvider = {
  getApiUrl: () => import.meta.env.VITE_API_URL || "http://localhost:8000/api",

  getList: async ({ resource, pagination, sorters, filters }) => {
    const params: Record<string, unknown> = {};

    if (pagination) {
      const { currentPage = 1, pageSize = 20 } = pagination;
      params.page = currentPage;
      params.page_size = pageSize;
    }

    if (sorters && sorters.length > 0) {
      params.sort_by = sorters[0].field;
      params.sort_order = sorters[0].order;
    }

    if (filters) {
      for (const filter of filters) {
        if ("field" in filter && filter.value !== undefined && filter.value !== null) {
          params[filter.field] = filter.value;
        }
      }
    }

    const { data, headers } = await api.get(`/${resource}`, { params });

    const headerCount = parseInt(headers["x-total-count"] ?? "", 10);
    const total = Number.isFinite(headerCount)
      ? headerCount
      : (Array.isArray(data) ? data.length : data.total ?? 0);

    return {
      data: Array.isArray(data) ? data : data.items ?? data.data ?? [],
      total,
    };
  },

  getOne: async ({ resource, id }) => {
    const { data } = await api.get(`/${resource}/${id}`);
    return { data };
  },

  create: async ({ resource, variables }) => {
    const { data } = await api.post(`/${resource}`, variables);
    return { data };
  },

  update: async ({ resource, id, variables }) => {
    const { data } = await api.patch(`/${resource}/${id}`, variables);
    return { data };
  },

  deleteOne: async ({ resource, id }) => {
    const { data } = await api.delete(`/${resource}/${id}`);
    return { data };
  },

  getMany: async ({ resource, ids }) => {
    // Backend has no bulk IDs endpoint — fetch individually in parallel
    const results = await Promise.all(
      ids.map((id) => api.get(`/${resource}/${id}`).then(({ data }) => data)),
    );
    return { data: results };
  },
};

export default dataProvider;
